import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import DOMPurify from "dompurify";
import { Mail, Download, Reply, Loader2, AlertCircle, RefreshCw, ChevronDown, ChevronRight, Send } from "lucide-react";
import { toast } from "sonner";

/** EmailThreadDialog — opens a full email conversation (oldest→newest),
 *  Sent / Received labelled, HTML sanitised, quoted-text collapse,
 *  attachments, inline reply box. Nothing sends until Send is clicked. */
export default function EmailViewerDialog({ emailId, open, onClose, onChanged }) {
  const [state, setState] = useState({ loading: false, thread: null, error: null });
  const [reply, setReply] = useState(""); const [sending, setSending] = useState(false);
  const [retrying, setRetrying] = useState({});
  const bottomRef = useRef(null);

  const load = async () => {
    if (!open || !emailId) return;
    setState({ loading: true, thread: null, error: null });
    try {
      const { data } = await api.get(`/emails/by-id/${emailId}/thread`);
      setState({ loading: false, thread: data, error: null });
      if (onChanged) onChanged();
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      setState({ loading: false, thread: null, error: e.response?.data?.detail || "Thread failed to load" });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [open, emailId]);

  const sendReply = async () => {
    if (!reply.trim() || !state.thread) return;
    setSending(true);
    try {
      await api.post(`/emails/threads/${state.thread.thread_id}/reply`, { body: reply.trim() });
      toast.success("Reply sent");
      setReply(""); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const retryFetch = async (mid) => {
    setRetrying((s) => ({ ...s, [mid]: true }));
    try {
      await api.post(`/emails/${mid}/retry-fetch`);
      toast.success("Fetched"); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Retry failed"); }
    finally { setRetrying((s) => ({ ...s, [mid]: false })); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="email-thread-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-sky-600"/>
            <span className="truncate">{state.thread?.subject || "Thread"}</span>
            {state.thread?.messages && (
              <span className="text-[10px] font-mono-accent uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-100 text-sky-800">
                {state.thread.messages.length} msg{state.thread.messages.length === 1 ? "" : "s"}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 pr-1" data-testid="email-thread-list">
          {state.loading && <div className="text-sm text-zinc-500 text-center py-8"><Loader2 className="w-4 h-4 animate-spin inline mr-2"/>Loading thread…</div>}
          {state.error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{state.error}</div>}
          {state.thread?.messages?.map((m) => (
            <MessageCard key={m.id} m={m}
                            onRetry={() => retryFetch(m.id)}
                            retrying={!!retrying[m.id]} />
          ))}
          <div ref={bottomRef}/>
        </div>

        {state.thread && (
          <div className="border-t border-zinc-200 pt-3 mt-2" data-testid="email-thread-reply-box">
            <div className="text-[10px] uppercase tracking-widest font-mono-accent text-zinc-500 mb-1">
              Reply · thread stays linked
            </div>
            <Textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)}
                           placeholder="Write a reply…" data-testid="email-thread-reply-input"/>
            <div className="flex items-center justify-end gap-2 mt-2">
              <Button variant="outline" onClick={onClose}>Close</Button>
              <Button onClick={sendReply} disabled={sending || !reply.trim()}
                           className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="email-thread-reply-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <Send className="w-4 h-4 mr-1"/>}
                Send reply
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MessageCard({ m, onRetry, retrying }) {
  const isInbound = m.direction === "inbound";
  const [showQuoted, setShowQuoted] = useState(false);
  const safeHtml = useMemo(() => {
    if (!m.html) return null;
    return DOMPurify.sanitize(m.html, { ADD_ATTR: ["target"] });
  }, [m.html]);

  // Split quoted lines (" > " prefix or "On <date>, <email> wrote:" marker)
  const { visibleText, quotedText } = useMemo(() => {
    const body = m.body || "";
    const idx = body.search(/\n\s*(On\s.+wrote:|-{2,}\s*Original Message)/i);
    if (idx > 0) return { visibleText: body.slice(0, idx).trim(), quotedText: body.slice(idx).trim() };
    const lines = body.split("\n");
    const qIdx = lines.findIndex((l) => l.trim().startsWith(">"));
    if (qIdx > 0) return { visibleText: lines.slice(0, qIdx).join("\n").trim(), quotedText: lines.slice(qIdx).join("\n").trim() };
    return { visibleText: body, quotedText: "" };
  }, [m.body]);

  return (
    <div className={`flex ${isInbound ? "justify-start" : "justify-end"}`} data-testid={`email-msg-${m.id}`}>
      <div className={`max-w-[85%] rounded-lg border p-3 ${isInbound ? "bg-sky-50 border-sky-200" : "bg-emerald-50 border-emerald-200"}`}>
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-[9px] font-mono-accent uppercase tracking-widest px-1.5 py-0.5 rounded ${isInbound ? "bg-sky-200 text-sky-900" : "bg-emerald-200 text-emerald-900"}`} data-testid={`email-msg-label-${m.id}`}>
            {isInbound ? "Received" : "Sent"}
          </span>
          <span className="text-xs text-zinc-700 font-mono-accent">{isInbound ? m.from_email : m.to_email}</span>
          <span className="text-[11px] text-zinc-500 ml-auto">{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</span>
        </div>
        {m.fetch_status === "failed" ? (
          <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2" data-testid={`email-msg-failed-${m.id}`}>
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5"/>
            <div className="flex-1">
              <div><strong>Email content could not be loaded.</strong></div>
              <div className="text-xs text-amber-700 mt-0.5">{m.fetch_error || "Resend fetch failed after 3 retries."}</div>
            </div>
            <Button size="sm" variant="outline" onClick={onRetry} disabled={retrying} data-testid={`email-msg-retry-${m.id}`}>
              {retrying ? <Loader2 className="w-3 h-3 animate-spin"/> : <RefreshCw className="w-3 h-3"/>}
            </Button>
          </div>
        ) : safeHtml ? (
          <div className="prose prose-sm max-w-none bg-white rounded border border-zinc-200 p-2"
                     dangerouslySetInnerHTML={{ __html: safeHtml }} data-testid={`email-msg-html-${m.id}`}/>
        ) : (
          <pre className="whitespace-pre-wrap text-sm font-sans text-zinc-800" data-testid={`email-msg-text-${m.id}`}>
            {visibleText || <em className="text-zinc-400">(empty)</em>}
          </pre>
        )}
        {quotedText && (
          <div className="mt-2">
            <button type="button" onClick={() => setShowQuoted((s) => !s)}
                        className="text-xs text-zinc-500 inline-flex items-center gap-1 hover:text-zinc-800"
                        data-testid={`email-msg-quoted-toggle-${m.id}`}>
              {showQuoted ? <ChevronDown className="w-3 h-3"/> : <ChevronRight className="w-3 h-3"/>}
              {showQuoted ? "Hide quoted text" : "Show quoted text"}
            </button>
            {showQuoted && (
              <pre className="mt-1 text-xs text-zinc-500 whitespace-pre-wrap pl-2 border-l-2 border-zinc-200">{quotedText}</pre>
            )}
          </div>
        )}
        {(m.attachments || []).length > 0 && (
          <ul className="mt-2 space-y-1">
            {m.attachments.map((a, i) => (
              <li key={i} className="flex items-center gap-2 text-xs">
                <Download className="w-3 h-3"/>
                <a href={a.url || a.href || "#"} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline truncate">
                  {a.filename || a.name || `attachment-${i + 1}`}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
